import { useId, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Label } from './label';

interface FormFieldProps {
  label: string;
  /** Message from React Hook Form, or a server-side field error. */
  error?: string;
  hint?: string;
  required?: boolean;
  className?: string;
  /**
   * Receives the ids and ARIA wiring to spread onto the control. Passing them
   * down rather than cloning children keeps this working with any input —
   * native, Radix, or a third-party editor.
   */
  children: (props: {
    id: string;
    'aria-invalid': boolean;
    'aria-describedby': string | undefined;
  }) => ReactNode;
}

/**
 * Label + control + hint + error, wired for accessibility.
 *
 * The error is rendered in a `role="alert"` region so a screen reader announces
 * a failed submit; a visually-styled red message with no ARIA is invisible to
 * anyone not looking at it.
 */
export function FormField({
  label,
  error,
  hint,
  required,
  className,
  children,
}: FormFieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');

  return (
    <div className={cn('grid gap-2', className)}>
      <Label htmlFor={id}>
        {label}
        {required && (
          <span className="ml-0.5 text-destructive" aria-hidden="true">
            *
          </span>
        )}
      </Label>

      {children({
        id,
        'aria-invalid': Boolean(error),
        'aria-describedby': describedBy || undefined,
      })}

      {hint && !error && (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      )}

      {error && (
        <p id={errorId} role="alert" className="text-xs font-medium text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
